'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { Heart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProductBase } from '@/types/product';
import { generateColor } from '@/utils/productMapper';
import { useAddToCart } from '@/hooks/useAddToCart';
import { CartItem } from '@/types/cart';
import { useCartTracking } from '@/hooks/useCartTracking';

// Función para convertir hex a rgba
function hexToRgba(hex: string, alpha: number): string {
  if (!hex || typeof hex !== 'string') {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  hex = hex.replace('#', '');

  if (hex.length !== 6) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const bigint = parseInt(hex, 16);

  if (isNaN(bigint)) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const validAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${r}, ${g}, ${b}, ${validAlpha})`;
}

// Función para formatear precios con coma
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

interface OtherCustomersProductsSectionMobileProps {
  className?: string;
  title?: string;
  subtitle?: string;
  products?: ProductBase[];
}

const OtherCustomersProductsSectionMobile: React.FC<OtherCustomersProductsSectionMobileProps> = ({
  className = '',
  title = 'OTROS CLIENTES TAMBIÉN COMPRARON',
  subtitle = 'Seguro que te interesa',
  products = [],
}) => {
  const router = useRouter();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { addToCart } = useAddToCart();
  const { trackAddToCartEvent } = useCartTracking();

  const handleFavoriteToggle = (id: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const handleAddToCart = (product: ProductBase) => {
    const cartItem: CartItem = {
      id: product.id.toString(),
      name: product.title,
      image: product.image,
      price: product.priceFinalInc,
      tax: product.priceFinalInc - product.priceFinalExc,
      quantity: 1,
      weight: product.weight || '',
      brand: product.brand || '',
      sku: product.slug,
      barcode: product.barcode || '',
    };
    addToCart(cartItem);
    trackAddToCartEvent(cartItem);
  };

  if (products.length === 0) {
    return null;
  }

  return (
    <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:96:4" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="96" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`py-2 ${className}`}>
      <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:97:6" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="97" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20mb-2%20text-left%22%7D" className="px-4 mb-2 text-left">
        <p data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:98:8" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="98" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-2xl%20leading-7%20text-global-1%20mb-1%22%7D" className="font-tt-norms-extra-black text-2xl leading-7 text-global-1 mb-1">{title}</p>
        <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:99:8" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="99" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-3%20inline-block%22%7D" className="bg-global-9 rounded px-3 inline-block">
          <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:100:10" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="100" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%20leading-4%20text-global-1%22%7D" className="font-tt-norms-medium text-sm leading-4 text-global-1">{subtitle}</span>
        </div>
      </div>

      <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:104:6" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="104" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20gap-2%20px-4%20mb-3%20items-stretch%22%7D" className="grid grid-cols-2 gap-2 px-4 mb-3 items-stretch">
        {products.map((product, index) => {
          // Calcular descuento (igual que en desktop)
          const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
          const originalPriceNumber =
            product?.priceOriginalInc !== undefined
              ? parseFloat(String(product?.priceOriginalInc))
              : undefined;
          const hasDiscount =
            originalPriceNumber !== undefined &&
            !isNaN(originalPriceNumber) &&
            originalPriceNumber > currentPriceNumber;
          const discountPercent = hasDiscount
            ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
            : 0;

          const backgroundColor = generateColor(index) || '#FFDE3B';
          const backgroundOpacity = 0.45; // Mismo que en GlutenFreeSectionMobile

          const backgroundStyle = {
            backgroundColor: hexToRgba(backgroundColor, backgroundOpacity),
          };

          return (
            <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:128:12" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="128" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B12px%5D%20p-3%20relative%20shadow-sm%20flex%20flex-col%20w-full%22%7D"
              key={product.id}
              className="rounded-[12px] p-3 relative shadow-sm flex flex-col w-full"
              style={{
                ...backgroundStyle,
                minHeight: '245px',
              }}
            >
              {/* Badge de descuento */}
              {hasDiscount && (
                <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:138:16" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="138" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-2%20right-2%20z-10%20bg-%5B%23fcc00a%5D%20px-2%20py-1%20rounded-full%20shadow-md%22%7D" className="absolute top-2 right-2 z-10 bg-[#fcc00a] px-2 py-1 rounded-full shadow-md">
                  <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:139:18" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="139" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-%5B10px%5D%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                    className="text-[#3d1c1a] text-[10px] font-bold"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700',
                    }}
                  >
                    -{discountPercent}%
                  </span>
                </div>
              )}

              {/* Imagen del producto */}
              <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:152:14" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="152" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-28%20mb-3%20flex-shrink-0%20mt-2%20overflow-hidden%22%7D" className="flex justify-center items-center h-28 mb-3 flex-shrink-0 mt-2 overflow-hidden">
                <Image data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:153:16" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="153" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%20cursor-pointer%22%7D"
                  src={product.image}
                  alt={product.title}
                  width={100}
                  height={100}
                  className="object-contain cursor-pointer"
                  onClick={() => router.push(`/${product.slug}`)}
                />
              </div>

              {/* Información del producto */}
              <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:164:14" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="164" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-left%20flex-1%20flex%20flex-col%20justify-between%22%7D" className="text-left flex-1 flex flex-col justify-between">
                <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:165:16" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="165" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <p data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:166:18" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="166" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-4%20text-%5B%233d1c1a%5D%20mb-1%20cursor-pointer%20line-clamp-2%20min-h-8%22%7D"
                    className="text-sm leading-4 text-[#3d1c1a] mb-1 cursor-pointer line-clamp-2 min-h-8"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700',
                    }}
                    onClick={() => router.push(`/${product.slug}`)}
                  >
                    {product.title}
                  </p>
                  {product.brand && (
                    <p data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:177:20" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="177" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%22%7D"
                      className="text-xs text-[#3d1c1a] mb-1"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                        fontWeight: '400',
                      }}
                    >
                      {product.brand}
                    </p>
                  )}
                  {product.productCombinations && product.productCombinations.length > 0 && (
                    <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:188:20" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="188" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%20block%22%7D"
                      className="text-xs text-[#3d1c1a] mb-1 block"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                        fontWeight: '400',
                      }}
                    >
                      {product.productCombinations
                        .find((combination) => combination.isDefault)
                        ?.productOptions?.map((option) => option.name)
                        .join(' / ')}
                    </span>
                  )}
                </div>

                {/* Precio */}
                <div data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:204:16" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="204" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20mt-auto%20pr-10%22%7D" className="flex flex-col mt-auto pr-10">
                    <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:205:20" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="205" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%233d1c1a%5D%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                      className="text-sm text-[#3d1c1a] whitespace-nowrap"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                        fontWeight: '700',
                      }}
                    >
                      {formatPrice(currentPriceNumber)} €/ud.
                    </span>
                    {hasDiscount && originalPriceNumber && (
                      <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:215:22" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="215" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20text-%5B%233d1c1a%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                        className="text-[10px] text-[#3d1c1a]/60 line-through"
                        style={{
                          fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                          fontWeight: 400,
                        }}
                      >
                        {formatPrice(originalPriceNumber)} €/ud.
                      </span>
                    )}
                </div>
              </div>

              <button data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:228:14" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="228" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20bottom-3%20right-3%20bg-%5B%23FFC700%5D%20rounded-%5B10px%5D%20w-8%20h-8%20flex%20items-center%20justify-center%20hover%3Abg-%5B%23FFC700%5D%2F90%20transition-all%20duration-300%20hover%3Ascale-110%20flex-shrink-0%22%7D"
                onClick={() => handleAddToCart(product)}
                className="absolute bottom-3 right-3 bg-[#FFC700] rounded-[10px] w-8 h-8 flex items-center justify-center hover:bg-[#FFC700]/90 transition-all duration-300 hover:scale-110 flex-shrink-0"
              >
                <span data-component-id="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx:232:16" data-component-path="src/components/sections/mobile/OtherCustomersProductsSectionMobile.tsx" data-component-line="232" data-component-file="OtherCustomersProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-lg%20font-bold%20leading-none%22%2C%22textContent%22%3A%22%2B%22%7D" className="text-[#3d1c1a] text-lg font-bold leading-none">+</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OtherCustomersProductsSectionMobile;
